// Mux streaming for our own <video> elements.
//
// We keep the real <video> (so our custom controls, <track> captions and every
// existing onPlay/onTimeUpdate handler keep working) and only change where its
// media comes from: Mux HLS via hls.js, or Safari's native HLS. If anything
// about the stream fails, the element falls back to the story's MP4 at the
// same position, so a Mux problem can never stop a story from playing.

let hlsModulePromise = null;
const loadHls = () => {
  if (!hlsModulePromise) {
    hlsModulePromise = import('hls.js').then((m) => m.default || m);
  }
  return hlsModulePromise;
};

let muxDataPromise = null;
const loadMuxData = () => {
  if (!muxDataPromise) {
    muxDataPromise = import('mux-embed').then((m) => m.default || m);
  }
  return muxDataPromise;
};

const HLS_MIME = 'application/vnd.apple.mpegurl';

// Safari (macOS + every iOS browser) plays HLS natively and does it better
// than MSE there (AirPlay, battery, PiP). Everyone else gets hls.js.
export const isAppleWebKit = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const safari = /Safari\//.test(ua) && !/Chrome\/|Chromium\/|CriOS\/|Edg\/|OPR\/|Android/.test(ua);
  return iOS || safari;
};

const HLS_CONFIG = {
  // Captions stay ours: the page renders <track> elements from /subtitles.
  // Parsing Mux's in-stream copy too would draw every line twice.
  enableWebVTT: false,
  enableIMSC1: false,
  enableCEA708Captions: false,
  capLevelToPlayerSize: true,
  maxBufferLength: 30,
};

/**
 * Point `video` at a URL and keep a play() that was asked for before the
 * source existed: assigning `src` runs the media load algorithm, which pauses
 * the element and rejects pending play promises.
 */
export function setSourceKeepingPlay(video, url) {
  const wantPlay = !video.paused;
  video.src = url;
  video.load();
  if (wantPlay) video.play().catch(() => {});
}

/**
 * Point `video` at a Mux HLS URL, falling back to `fallbackUrl` on failure.
 * Returns { detach(), engine, hls, Hls } once the source is attached.
 *  lazy: don't download media until the viewer presses play (previews).
 *  isCancelled: checked after async work so a stale attach never lands.
 */
export async function attachStream(video, { hlsUrl, fallbackUrl, onFallback, lazy = false, isCancelled = () => false }) {
  let hls = null;
  let Hls = null;
  let fellBack = false;
  let nativeErrorHandler = null;
  let lazyStart = null;
  const wantPlay = !video.paused;

  const fallBack = (reason) => {
    if (fellBack || !fallbackUrl) return;
    fellBack = true;
    const resumeAt = video.currentTime || 0;
    const wasPlaying = !video.paused;
    if (hls) {
      try { hls.destroy(); } catch (_) { /* already gone */ }
      hls = null;
    }
    if (nativeErrorHandler) video.removeEventListener('error', nativeErrorHandler);
    if (lazyStart) video.removeEventListener('play', lazyStart);
    video.src = fallbackUrl;
    const restore = () => {
      video.removeEventListener('loadedmetadata', restore);
      if (resumeAt > 0) {
        try { video.currentTime = resumeAt; } catch (_) { /* not seekable yet */ }
      }
      if (wasPlaying) video.play().catch(() => {});
    };
    video.addEventListener('loadedmetadata', restore);
    video.load();
    if (onFallback) onFallback(reason);
  };

  const useNative = isAppleWebKit() && video.canPlayType(HLS_MIME);
  if (!useNative) {
    try {
      Hls = await loadHls();
    } catch (_) {
      Hls = null;
    }
    if (isCancelled()) return { engine: 'cancelled', hls: null, Hls: null, detach() {} };
  }

  if (!useNative && Hls && Hls.isSupported()) {
    hls = new Hls({ ...HLS_CONFIG, autoStartLoad: !lazy });
    if (lazy) {
      lazyStart = () => {
        video.removeEventListener('play', lazyStart);
        lazyStart = null;
        if (hls) hls.startLoad();
      };
      video.addEventListener('play', lazyStart);
    }
    if (wantPlay) {
      hls.once(Hls.Events.MANIFEST_PARSED, () => {
        if (hls && lazy) hls.startLoad();
        video.play().catch(() => {});
      });
    }
    let recoveredMedia = false;
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!data || !data.fatal) return;
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recoveredMedia) {
        recoveredMedia = true;
        hls.recoverMediaError();
        return;
      }
      fallBack(`hls:${data.type}:${data.details}`);
    });
    hls.loadSource(hlsUrl);
    hls.attachMedia(video);
  } else if (video.canPlayType(HLS_MIME)) {
    nativeErrorHandler = () => {
      // An error on the Mux URL (expired token, network) -> MP4.
      if (video.currentSrc && video.currentSrc.includes('.m3u8')) fallBack('native-error');
    };
    video.addEventListener('error', nativeErrorHandler);
    setSourceKeepingPlay(video, hlsUrl);
  } else {
    fallBack('no-hls-support');
  }

  return {
    engine: hls ? 'hls.js' : fellBack ? 'mp4' : 'native',
    hls,
    Hls,
    detach() {
      if (nativeErrorHandler) video.removeEventListener('error', nativeErrorHandler);
      if (lazyStart) video.removeEventListener('play', lazyStart);
      if (hls) {
        try { hls.destroy(); } catch (_) { /* ignore */ }
        hls = null;
      }
    },
  };
}

/**
 * Report this view to Mux Data (startup time, rebuffering, errors, engagement
 * heatmaps keyed by video_id). Only the story id is sent: no title (it often
 * holds a child's name), no viewer id, and no Mux cookie.
 */
export async function startMuxData(video, { envKey, videoId, playerName, hls, Hls, initTime }) {
  if (!envKey || !video) return () => {};
  let mux;
  try {
    mux = await loadMuxData();
  } catch (_) {
    return () => {};
  }
  try {
    mux.monitor(video, {
      debug: false,
      disableCookies: true,
      ...(hls ? { hlsjs: hls, Hls } : {}),
      data: {
        env_key: envKey,
        player_name: playerName || 'pixley-web',
        player_init_time: initTime || Date.now(),
        video_id: videoId,
        video_stream_type: 'on-demand',
      },
    });
  } catch (_) {
    return () => {};
  }
  return () => {
    try { mux.destroyMonitor(video); } catch (_) { /* ignore */ }
  };
}

/**
 * GET /stories/{id}/playback — null on any failure (caller uses the MP4).
 * create=true asks the backend to make the Mux copy (sent once someone
 * actually plays), a plain call only reads.
 */
export async function fetchPlayback(apiClient, contentId, shareToken, { create = false } = {}) {
  if (!apiClient || !contentId) return null;
  const params = new URLSearchParams();
  if (shareToken) params.set('token', shareToken);
  if (create) params.set('create', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  try {
    const res = await apiClient.request(`/stories/${contentId}/playback${qs}`, { method: 'GET' });
    return res?.data || null;
  } catch (_) {
    return null;
  }
}

// When the backend says Mux is off for this viewer, remember it briefly so
// every other player on the page starts its MP4 without waiting.
const OFF_KEY = 'pixley_mux_off_until';
export const muxKnownOff = () => {
  try {
    return Number(sessionStorage.getItem(OFF_KEY) || 0) > Date.now();
  } catch (_) {
    return false;
  }
};
export const rememberMuxOff = () => {
  try {
    sessionStorage.setItem(OFF_KEY, String(Date.now() + 10 * 60 * 1000));
  } catch (_) {
    /* storage unavailable: just ask again next time */
  }
};

// ── Audio languages (dubbed tracks on the Mux stream) ──────────────────────
const AUDIO_PREF_KEY = 'pixley_audio_lang';
export const preferredAudioLanguage = () => {
  try {
    return localStorage.getItem(AUDIO_PREF_KEY) || null;
  } catch (_) {
    return null;
  }
};
export const rememberAudioLanguage = (lang) => {
  try {
    if (lang) localStorage.setItem(AUDIO_PREF_KEY, lang);
  } catch (_) {
    /* per-viewer convenience only */
  }
};

const baseLang = (code) => String(code || '').toLowerCase().split('-')[0];

/**
 * Watch a stream's audio tracks (hls.js or Safari native) and let the viewer
 * switch. onChange({tracks:[{id,lang,label}], current}) fires on any change.
 * Returns { select(id), stop() }. The viewer's last pick is remembered and
 * applied automatically when a story offers that language.
 */
export function watchAudioTracks(video, stream, onChange) {
  const hls = stream?.hls;
  const Hls = stream?.Hls;
  let applied = false;

  const read = () => {
    if (hls) {
      const tracks = (hls.audioTracks || []).map((t, i) => ({ id: i, lang: baseLang(t.lang), label: t.lang ? t.name || t.lang : 'Original' }));
      return { tracks, current: hls.audioTrack };
    }
    const list = video.audioTracks;
    if (!list) return { tracks: [], current: null };
    const tracks = [];
    let current = null;
    for (let i = 0; i < list.length; i++) {
      tracks.push({ id: i, lang: baseLang(list[i].language), label: list[i].language ? list[i].label || list[i].language : 'Original' });
      if (list[i].enabled) current = i;
    }
    return { tracks, current };
  };

  const select = (id, { remember = true } = {}) => {
    const { tracks } = read();
    const pick = tracks.find((t) => t.id === id);
    if (!pick) return;
    if (hls) {
      hls.audioTrack = id;
    } else if (video.audioTracks) {
      for (let i = 0; i < video.audioTracks.length; i++) video.audioTracks[i].enabled = i === id;
    }
    if (remember) rememberAudioLanguage(pick.lang);
    onChange(read());
  };

  const refresh = () => {
    const state = read();
    if (!applied && state.tracks.length > 1) {
      applied = true;
      const want = preferredAudioLanguage();
      const match = want && state.tracks.find((t) => t.lang === want);
      if (match && match.id !== state.current) {
        select(match.id, { remember: false });
        return;
      }
    }
    onChange(state);
  };

  if (hls && Hls) {
    hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, refresh);
    hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, refresh);
  } else if (video.audioTracks) {
    video.audioTracks.addEventListener('addtrack', refresh);
    video.audioTracks.addEventListener('change', refresh);
  }
  refresh();

  return {
    select,
    stop() {
      if (hls && Hls) {
        hls.off(Hls.Events.AUDIO_TRACKS_UPDATED, refresh);
        hls.off(Hls.Events.AUDIO_TRACK_SWITCHED, refresh);
      } else if (video.audioTracks) {
        video.audioTracks.removeEventListener('addtrack', refresh);
        video.audioTracks.removeEventListener('change', refresh);
      }
    },
  };
}

// ── Viewer-requested dubs ───────────────────────────────────────────────────
const qs = (params) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && p.set(k, v));
  const str = p.toString();
  return str ? `?${str}` : '';
};

/** GET /stories/{id}/dubs -> {languages:[{code,status}], can_request, my_active} or null */
export async function fetchDubs(apiClient, contentId, shareToken) {
  try {
    const res = await apiClient.request(`/stories/${contentId}/dubs${qs({ token: shareToken })}`, { method: 'GET' });
    return res?.data || null;
  } catch (_) {
    return null;
  }
}

/** POST /stories/{id}/dubs. Resolves {state} or throws with the server's message. */
export async function requestDub(apiClient, contentId, language, shareToken) {
  const res = await apiClient.request(`/stories/${contentId}/dubs${qs({ token: shareToken })}`, {
    method: 'POST',
    body: JSON.stringify({ language }),
  });
  // apiClient.request resolves {success:false, error} instead of throwing.
  if (!res || res.success === false) throw new Error(res?.error || "Couldn't start that dub.");
  return res.data || {};
}
